import { TriangleAlert } from 'lucide-react';
import { useStore } from '@nanostores/react';

import { useUserInfoQuery } from '@/entities/user-info';
import { useCareStateDomain } from '@/entities/care-state-domain';
import { PhrTableTab } from '@/features/phr-table';
import {
  $patientWorkspaceSearchTarget,
  clearPatientWorkspaceSearchTarget,
} from '@/shared/stores/ui';

import { CareStateLabOrdersTable } from './CareStateLabOrdersTable';

interface PatientLabsTabProps {
  userId: string;
}

export function PatientLabsTab({ userId }: PatientLabsTabProps) {
  const userInfoQuery = useUserInfoQuery(userId);
  const insurance = userInfoQuery.data?.patientInfo?.insurance;
  const hasInsurance = insurance !== null && insurance !== undefined && insurance !== '';
  const searchTarget = useStore($patientWorkspaceSearchTarget);
  const openOrderId =
    searchTarget !== null &&
    searchTarget.tabId === 'phr:labs' &&
    searchTarget.resource === 'care-state:lab-orders'
      ? searchTarget.resultId
      : undefined;
  const { careState } = useCareStateDomain();
  const hasLabOrders = (careState?.lab_orders?.length ?? 0) > 0;

  return (
    <div className="space-y-6" aria-label="Labs">
      {!hasInsurance && !userInfoQuery.isLoading && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            This patient does not have insurance information, so labs might not be possible to
            submit.
          </span>
        </div>
      )}

      {hasLabOrders && (
        <section aria-label="Ordered through Lotus Health">
          <h2 className="text-base font-medium leading-tight tracking-tight">
            Ordered Through Lotus Health
          </h2>
          <div className="mt-3">
            <CareStateLabOrdersTable
              openOrderId={openOrderId}
              onConsumeOpenOrder={clearPatientWorkspaceSearchTarget}
            />
          </div>
        </section>
      )}

      <section aria-label="EHR labs">
        <h2 className="text-base font-medium leading-tight tracking-tight">EHR Labs</h2>
        <div className="mt-3">
          <PhrTableTab userId={userId} tableKey="labs" />
        </div>
      </section>
    </div>
  );
}
